// Potok powiadomień (Mercury `notifications_worker`, stan w Mongo `BestBiznesNotifications`,
// ustawienia w Supabase). Kształty odpowiedzi `/api/v1/admin/notifications/*` — patrz
// `crates/admin_ai_tools/src/api/notifications.rs`.

export type DeliveryStatus = "pending" | "sending" | "sent" | "failed" | "bounced" | "expired";
export type DeliveryChannel = "email" | "sms";

export interface NotificationCounts {
  deliveries_total: number;
  deliveries_by_status: Partial<Record<DeliveryStatus, number>>;
  deliveries_last_24h: number;
  deliveries_failed_24h: number;
  last_delivery_at: string | null;
  /** Wpisy w aplikacji z ostatnich 24 h per typ (`daily_raport`, `instant_raport`, …). */
  in_app_last_24h_by_type: Record<string, number>;
  reports_last_24h: number;
  /** Notatki czekające na zbiorczy mail godzinny. */
  pending_note_mail: number;
  outbox: number;
}

export interface RuntimeFlag {
  key: string;
  value: string;
}

export interface NotificationsDashboardResponse {
  counts: NotificationCounts;
  flags: RuntimeFlag[];
  togglable_flags: string[];
}

export interface DeliveryRow {
  id: string;
  user_id: string;
  channel: DeliveryChannel;
  /** `window` (mail okresowy / godzinny), `notes`, `problem`, `legacy`. */
  kind: string;
  notification_types: string[];
  status: DeliveryStatus;
  subject: string;
  attempts: number;
  last_error: string | null;
  created_at: string | null;
  sent_at: string | null;
  window_start: string | null;
  window_end: string | null;
  email: string | null;
  phone: string | null;
  has_body: boolean;
  item_count: number;
}

export interface DeliveriesResponse {
  data: DeliveryRow[];
}

export interface AuctionPayload {
  pretty_id: string;
  subject?: string | null;
  organizer?: string | null;
  submitting_date?: string | null;
  created_at?: string | null;
  modified_at?: string | null;
}

export interface DeliveryDetail {
  delivery: {
    id: string;
    user_id: string;
    channel: DeliveryChannel;
    kind: string;
    notification_types: string[];
    subject: string | null;
    status: DeliveryStatus;
    attempts: number;
    last_error: string | null;
    idempotency_key: string;
    provider_message_id: string | null;
    created_at: string;
    claimed_at: string | null;
    sent_at: string | null;
    window_start: string | null;
    window_end: string | null;
    body_html: string | null;
    body_text: string | null;
    body_purged_at: string | null;
  };
  recipient: {
    user_id: string;
    email: string | null;
    phone: string | null;
    /** Kanały odbiorcy dla typów tej wysyłki (`UserNotificationPreferences` + domyślne). */
    channels: { type: string; method: string; enabled: boolean }[];
  };
  /** Snapshot sekcji maila — po jednej na filtr. */
  items: {
    filter_id: string;
    filter_name: string;
    report_id: string | null;
    report_pretty_id: string | null;
    count: number;
    new_count: number;
    change_count: number;
    new_payloads: AuctionPayload[];
    change_payloads: AuctionPayload[];
  }[];
  /** Notatki zbiorczego maila (`kind = notes`). */
  notes: { id: string; type: string; title: string; auction_pretty_id: string | null; created_at: string }[];
}

export interface DeliveryDetailResponse {
  data: DeliveryDetail;
}

/** Kto dostaje co — wiersz `notification_filter_subscribers` (Supabase). */
export interface SubscriberRow {
  employee_id: string;
  employer_id: string;
  filter_id: string;
  filter_name: string | null;
  filter_owner_id: string | null;
  interval: string;
  can_sms: boolean;
}

export interface SubscribersResponse {
  data: SubscriberRow[];
}

export const DELIVERY_STATUS_COLORS: Record<DeliveryStatus, string> = {
  pending: "default",
  sending: "processing",
  sent: "success",
  failed: "error",
  bounced: "warning",
  expired: "default",
};

/** Opisy flag runtime — panel pokazuje je obok przełącznika. */
export const FLAG_DESCRIPTIONS: Record<string, string> = {
  notify_global_pause: "Hamulec awaryjny — sender nie renderuje i nie wysyła niczego.",
  send_dry_run: "Wysyłka na sucho — treść jest renderowana i zapisywana, ale nic nie trafia do SES/OVH.",
  search_hybrid: "Wyszukiwarka: noga kNN/RRF (hybrydowe wyszukiwanie).",
  search_engine: "Wyszukiwarka: wybór silnika.",
};

/** `null`/brak daty → „—”; mikrosekundy z backendu obcięte do milisekund. */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value.replace(/(\.\d{3})\d+/, "$1"));
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString("pl-PL");
}
